import React from "react";
import PermissionsGate from "../../../utils/permissionGate";
import { tokens } from "../../../public/tokens";
import "./Button.css";

/**
 * A versatile button component that supports various styles, sizes, loading states, and permission-based rendering.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.variant="primary"] - Button style variant.
 * @param {string} [props.size="md"] - Button size.
 * @param {boolean} [props.isLoading=false] - Shows loading spinner.
 * @param {boolean} [props.disabled=false] - Disables the button.
 * @param {Function} [props.onClick] - Click handler.
 * @param {React.ReactNode} [props.children] - Button text/content.
 * @param {string} [props.type="button"] - Button type.
 * @param {boolean} [props.fullWidth=false] - Makes the button full width.
 * @param {React.ReactNode} [props.iconLeft=null] - Icon shown before the text.
 * @param {React.ReactNode} [props.iconRight=null] - Icon shown after the text.
 * @param {boolean} [props.iconOnly=false] - Hides text and renders icon-only button.
 * @param {string} [props.className=""] - Additional CSS classes.
 * @param {Object} [props.style={}] - Additional inline styles.
 * @param {string|null} [props.shape=null] - Optional shape styling.
 * @param {Array} [props.permissions=[]] - Permission scopes required to use the button.
 * @returns {JSX.Element}
 */
const Button = ({
  variant = "primary",
  size = "md",
  isLoading = false,
  disabled = false,
  onClick,
  children,
  type = "button",
  fullWidth = false,
  iconLeft = null,
  iconRight = null,
  iconOnly = false,
  className = "",
  style = {},
  shape = null,
  permissions = [],
  ...rest
}) => {
  const shapeStyles = {
    circle: {
      borderRadius: tokens.globals["border-radius"],
    },
  };

  const buttonClasses = [
    "aie-button",
    `aie-button--${variant}`,
    `aie-button--${size}`,
    fullWidth ? "aie-button--full-width" : "",
    iconOnly ? "aie-button--icon-only" : "",
    isLoading ? "aie-button--loading" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const buttonStyle = {
    ...(shapeStyles[shape] || {}),
    ...style,
  };

  const buttonElement = (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={buttonClasses}
      style={buttonStyle}
      {...rest}
    >
      {isLoading && <i className="fas fa-spinner fa-spin aie-button__spinner" />}
      {!isLoading && iconLeft && (
        <span className="aie-button__icon-left">{iconLeft}</span>
      )}
      {!iconOnly && children && (
        <span className="aie-button__text">{children}</span>
      )}
      {!isLoading && iconRight && (
        <span className="aie-button__icon-right">{iconRight}</span>
      )}
    </button>
  );

  if (permissions && permissions.length > 0) {
    return (
      <PermissionsGate scopes={permissions} errorProps={{ disabled: true }}>
        {buttonElement}
      </PermissionsGate>
    );
  }

  return buttonElement;
};

export default Button;